import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api/v1';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  let token = localStorage.getItem('authToken');
  
  // Auto-set admin token for development if no token exists
  if (!token) {
    token = 'test-admin-token';
    localStorage.setItem('authToken', token);
  }
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// New Channel-based Types
export interface Channel {
  _id: string;
  name: string;
  description?: string;
  category?: string;
  status: 'active' | 'inactive' | 'draft' | 'archived';
  frequency?: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';
  timezone?: string;
  logoUrl?: string;
  bannerUrl?: string;
  colorScheme?: Record<string, string>;
  isPublic: boolean;
  allowComments: boolean;
  moderationLevel: string;
  ownerId: string;
  moderators: string[];
  createdBy?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Show {
  _id: string;
  channelId: string;
  name: string;
  description?: string;
  category?: string;
  status: 'active' | 'inactive' | 'draft' | 'archived';
  frequency?: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';
  customSchedule?: Record<string, any>;
  timezone?: string;
  newsCriteria: Record<string, any>;
  panelistIds: string[];
  discussionSettings: Record<string, any>;
  logoUrl?: string;
  introAudioUrl?: string;
  outroAudioUrl?: string;
  createdBy?: string;
  isPublic: boolean;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Episode {
  _id: string;
  showId: string;
  channelId: string;
  title: string;
  description?: string;
  episodeNumber?: number;
  seasonNumber?: number;
  status: 'draft' | 'active' | 'inactive' | 'archived' | 'published';
  publishDate?: string;
  publishedAt?: string;
  duration?: string;
  wordCount?: number;
  segmentCount?: number;
  isPublic: boolean;
  allowComments: boolean;
  createdBy?: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

// Legacy types for backward compatibility
export interface NewsShow {
  _id: string;
  name: string;
  description: string;
  category: string;
  panelistIds: string[];
  discussionSettings: {
    duration: string;
    style: string;
    tone: string;
  };
  status: 'draft' | 'active' | 'archived';
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface Panelist {
  _id: string;
  name: string;
  gender: string;
  age?: number;
  ttsProvider: string;
  voiceId?: string;
  voiceName: string;
  languageCode: string;
  openaiVoiceSettings: {
    speed?: number;
    format?: string;
    instructions?: string;
  };
  character: string;
  background?: string;
  expertise: string[];
  discussionStyle: {
    tone?: string;
    pace?: string;
    style?: string;
    approach?: string;
    speaking_style?: string;
    formality?: string;
  };
  headshotUrl?: string;
  createdBy?: string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ShowSegment {
  _id: string;
  episodeId: string; // Changed from showId to episodeId
  title: string;
  description: string;
  order: number;
  script: string | null;
  scriptParts: ScriptPart[];
  audioUrl: string | null;
  audioDuration: number | null;
  status: 'draft' | 'script_ready' | 'audio_ready' | 'published';
  scriptGenerationId: string | null;
  audioGenerationStatus: 'pending' | 'generating' | 'completed' | 'failed';
  audioGenerationError: string | null;
  createdBy: string;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ScriptPart {
  id: string;
  panelistId: string;
  panelistName: string;
  script: string;
  order: number;
  audioUrl?: string;
}

export interface NewsArticle {
  _id: string;
  title: string;
  description: string;
  content: string;
  fullContents: string;
  url: string;
  urlToImage: string;
  publishedAt: string;
  source: {
    id: string;
    name: string;
  };
  category: string;
  language: string;
  contentExtractedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExternalNewsArticle {
  uuid: string;
  title: string;
  description: string;
  content: string;
  url: string;
  image_url?: string;
  published_at: string;
  source: string;
  source_id: string;
  categories: string[];
  language: string;
  relevance_score?: number;
}

export interface ExternalNewsSearchResult {
  data: ExternalNewsArticle[];
  meta?: {
    found: number;
    returned: number;
    limit: number;
    page: number;
  };
}

export interface NewsItem {
  _id: string;
  segmentId: string;
  newsArticleId: string;
  order: number;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  article?: NewsArticle; // Optional: populated when fetching with article details
}

export interface SourceMaterial {
  _id: string;
  newsItemId: string;
  title: string;
  content: string;
  sourceUrl: string;
  type: 'manual_text' | 'external_url' | 'news_article';
  addedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ScriptGeneration {
  _id: string;
  showSegmentId: string;
  showId: string;
  status: 'pending' | 'generating' | 'completed' | 'failed';
  script: string | null;
  scriptParts: ScriptPart[];
  errorMessage: string | null;
  requestedBy: string;
  requestedAt: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Voice {
  _id: string;
  name: string;
  enabled: boolean;
  languageCodes: string[];
  naturalSampleRateHertz: number;
  ssmlGender: string;
  source: string;
  createdAt: string;
  updatedAt: string;
}

// API Service Class
class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
    this.api = api;
  }

  // Channel APIs
  async getChannels(limit = 50, skip = 0, status?: string, category?: string): Promise<Channel[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (skip) params.append('skip', skip.toString());
    if (status) params.append('status', status);
    if (category) params.append('category', category);
    
    const response = await this.api.get(`/channels/?${params.toString()}`);
    return response.data;
  }

  async getChannel(id: string): Promise<Channel> {
    const response = await this.api.get(`/channels/${id}`);
    return response.data;
  }

  async createChannel(channelData: Partial<Channel>): Promise<Channel> {
    const response = await this.api.post('/channels/', channelData);
    return response.data;
  }

  async updateChannel(id: string, channelData: Partial<Channel>): Promise<Channel> {
    const response = await this.api.put(`/channels/${id}`, channelData);
    return response.data;
  }

  async deleteChannel(id: string): Promise<void> {
    await this.api.delete(`/channels/${id}`);
  }

  // Show APIs
  async getShows(limit = 50, skip = 0, channelId?: string): Promise<Show[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (skip) params.append('skip', skip.toString());
    if (channelId) params.append('channel_id', channelId);
    
    const response = await this.api.get(`/shows-new/?${params.toString()}`);
    return response.data;
  }

  async getShow(id: string): Promise<Show> {
    const response = await this.api.get(`/shows-new/${id}`);
    return response.data;
  }

  async createShow(showData: Partial<Show>): Promise<Show> {
    const response = await this.api.post('/shows-new/', showData);
    return response.data;
  }

  async updateShow(id: string, showData: Partial<Show>): Promise<Show> {
    const response = await this.api.put(`/shows-new/${id}`, showData);
    return response.data;
  }

  async deleteShow(id: string): Promise<void> {
    await this.api.delete(`/shows-new/${id}`);
  }

  // Episode APIs
  async getEpisodes(limit = 50, skip = 0, showId?: string, channelId?: string): Promise<Episode[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (skip) params.append('skip', skip.toString());
    if (showId) params.append('show_id', showId);
    if (channelId) params.append('channel_id', channelId);
    
    const response = await this.api.get(`/episodes/?${params.toString()}`);
    return response.data;
  }

  async getEpisode(id: string): Promise<Episode> {
    const response = await this.api.get(`/episodes/${id}`);
    return response.data;
  }

  async createEpisode(episodeData: Partial<Episode>): Promise<Episode> {
    const response = await this.api.post('/episodes/', episodeData);
    return response.data;
  }

  async updateEpisode(id: string, episodeData: Partial<Episode>): Promise<Episode> {
    const response = await this.api.put(`/episodes/${id}`, episodeData);
    return response.data;
  }

  async deleteEpisode(id: string): Promise<void> {
    await this.api.delete(`/episodes/${id}`);
  }

  async publishEpisode(id: string): Promise<Episode> {
    const response = await this.api.post(`/episodes/${id}/publish`);
    return response.data;
  }

  async unpublishEpisode(id: string): Promise<Episode> {
    const response = await this.api.post(`/episodes/${id}/unpublish`);
    return response.data;
  }

  // Legacy APIs for backward compatibility
  async getLegacyShows(limit = 50, skip = 0): Promise<NewsShow[]> {
    const response = await this.api.get(`/shows?limit=${limit}&skip=${skip}`);
    return response.data;
  }

  async getLegacyShow(id: string): Promise<NewsShow> {
    const response = await this.api.get(`/shows/${id}`);
    return response.data;
  }

  async updateLegacyShow(id: string, showData: Partial<NewsShow>): Promise<NewsShow> {
    const response = await this.api.put(`/shows/${id}`, showData);
    return response.data;
  }

  // Panelist APIs
  async getPanelists(limit = 50, skip = 0): Promise<Panelist[]> {
    const response = await this.api.get(`/panelists?limit=${limit}&skip=${skip}`);
    return response.data;
  }

  async getPanelist(id: string): Promise<Panelist> {
    const response = await this.api.get(`/panelists/${id}`);
    return response.data;
  }

  async createPanelist(panelistData: Partial<Panelist>): Promise<Panelist> {
    const response = await this.api.post('/panelists/', panelistData);
    return response.data;
  }

  async updatePanelist(id: string, panelistData: Partial<Panelist>): Promise<Panelist> {
    const response = await this.api.put(`/panelists/${id}`, panelistData);
    return response.data;
  }

  async deletePanelist(id: string): Promise<void> {
    await this.api.delete(`/panelists/${id}`);
  }

  async uploadPanelistHeadshot(id: string, file: File): Promise<{ headshotUrl: string; message: string }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.api.post(`/panelists/${id}/headshot`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deletePanelistHeadshot(id: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/panelists/${id}/headshot`);
    return response.data;
  }

  getPanelistHeadshotUrl(id: string): string {
    // For images, we need the full URL to the backend server
    const backendUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
    return `${backendUrl}/panelists/${id}/headshot`;
  }

  // Segment APIs
  async getShowSegments(episodeId: string): Promise<ShowSegment[]> {
    const response = await this.api.get(`/episodes/${episodeId}/segments`);
    return response.data;
  }

  async getShowSegment(segmentId: string): Promise<ShowSegment> {
    const response = await this.api.get(`/segments/${segmentId}`);
    return response.data;
  }

  async createShowSegment(segmentData: Partial<ShowSegment>): Promise<ShowSegment> {
    // Extract episodeId from segmentData
    const { episodeId, ...data } = segmentData;
    if (!episodeId) {
      throw new Error('episodeId is required to create a segment');
    }
    
    const response = await this.api.post(`/episodes/${episodeId}/segments`, data);
    return response.data;
  }

  async updateShowSegment(id: string, segmentData: Partial<ShowSegment>): Promise<ShowSegment> {
    const response = await this.api.put(`/segments/${id}`, segmentData);
    return response.data;
  }

  async deleteShowSegment(id: string): Promise<void> {
    await this.api.delete(`/segments/${id}`);
  }

  // News Item APIs
  async getSegmentNewsItems(segmentId: string): Promise<NewsItem[]> {
    const response = await this.api.get(`/segments/${segmentId}/news-items`);
    return response.data;
  }

  async addNewsItemToSegment(segmentId: string, articleData: any): Promise<NewsItem> {
    const response = await this.api.post(`/segments/${segmentId}/news-items`, articleData);
    return response.data;
  }

  async deleteNewsItemFromSegment(segmentId: string, newsItemId: string): Promise<void> {
    await this.api.delete(`/segments/${segmentId}/news-items/${newsItemId}`);
  }

  async reorderNewsItemsInSegment(segmentId: string, newsItemIds: string[]): Promise<void> {
    await this.api.put(`/segments/${segmentId}/news-items/reorder`, newsItemIds);
  }

  // Script Generation APIs
  async generateScript(segmentId: string, requestData: any): Promise<any> {
    const response = await this.api.post(`/segments/${segmentId}/generate-script`, requestData);
    return response.data;
  }

  // Audio Generation APIs
  async requestAudioGeneration(segmentId: string): Promise<any> {
    const response = await this.api.post(`/segments/${segmentId}/generate-audio`, {});
    return response.data;
  }

  // Audio APIs
  async getAudioFile(segmentId: string, partIndex: number): Promise<Blob> {
    const response = await this.api.get(`/audio/segments/${segmentId}/${partIndex}`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async getSegmentAudio(segmentId: string): Promise<Blob> {
    const response = await this.api.get(`/audio/segments/${segmentId}/main`, {
      responseType: 'blob'
    });
    return response.data;
  }

  getAudioFileUrl(segmentId: string, partIndex: number): string {
    return `${this.baseURL}/audio/segments/${segmentId}/${partIndex}`;
  }

  // Session APIs
  async startSession(showId: string, sessionName: string): Promise<{
    session: { _id: string; sessionName: string; showId: string; status: string };
    show: NewsShow;
    panelists: Panelist[];
    segments: ShowSegment[];
    message: string;
  }> {
    const response = await this.api.post('/sessions/', {
      showId,
      sessionName
    });
    return response.data;
  }

  async getSessionStatus(sessionId: string): Promise<any> {
    const response = await this.api.get(`/sessions/${sessionId}/status`);
    return response.data;
  }

  async getSessionPlaylist(sessionId: string): Promise<any> {
    const response = await this.api.get(`/sessions/${sessionId}/playlist`);
    return response.data;
  }

  async getDiscussionSession(sessionId: string): Promise<any> {
    const response = await this.api.get(`/sessions/${sessionId}/discussion`);
    return response.data;
  }

  async generateNextDiscussion(sessionId: string, articleId?: string): Promise<any> {
    const response = await this.api.post(`/sessions/${sessionId}/generate-discussion`, {
      articleId
    });
    return response.data;
  }

  async generateConclusion(sessionId: string): Promise<any> {
    const response = await this.api.post(`/sessions/${sessionId}/generate-conclusion`);
    return response.data;
  }

  async getActiveSessions(limit = 20): Promise<any[]> {
    const response = await this.api.get(`/sessions/active?limit=${limit}`);
    return response.data;
  }

  async pauseSession(sessionId: string): Promise<void> {
    await this.api.post(`/sessions/${sessionId}/pause`);
  }

  async resumeSession(sessionId: string): Promise<void> {
    await this.api.post(`/sessions/${sessionId}/resume`);
  }

  async skipCurrentArticle(sessionId: string): Promise<void> {
    await this.api.post(`/sessions/${sessionId}/skip-article`);
  }

  async endSession(sessionId: string): Promise<void> {
    await this.api.post(`/sessions/${sessionId}/end`);
  }

  // News APIs
  async getNewsArticles(limit = 50, skip = 0, category?: string): Promise<NewsArticle[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (skip) params.append('skip', skip.toString());
    if (category) params.append('category', category);
    
    const response = await this.api.get(`/news?${params.toString()}`);
    return response.data;
  }

  async searchNews(query: string, limit = 20): Promise<NewsArticle[]> {
    const response = await this.api.get(`/news-search/search?query=${encodeURIComponent(query)}&limit=${limit}`);
    return response.data.articles || [];
  }

  async getExternalNews(query: string, limit = 20): Promise<ExternalNewsSearchResult> {
    const response = await this.api.get(`/external-news/search?search=${encodeURIComponent(query)}&limit=${limit}`);
    // Transform the response to match the expected interface
    return {
      data: response.data.articles || [],
      meta: {
        found: response.data.total || 0,
        returned: response.data.articles?.length || 0,
        limit: limit,
        page: 1
      }
    };
  }

  // Voice APIs
  async getVoices(): Promise<Voice[]> {
    const response = await this.api.get('/voices/');
    return response.data;
  }

  // Missing API methods for ShowDetail components
  async getNewsItems(segmentId: string): Promise<any> {
    const response = await this.api.get(`/segments/${segmentId}/news-items`);
    return response.data;
  }

  async addNewsItem(segmentId: string, articleId: string): Promise<any> {
    const response = await this.api.post(`/segments/${segmentId}/news-items`, { articleId });
    return response.data;
  }

  async removeNewsItem(segmentId: string, newsItemId: string): Promise<void> {
    await this.api.delete(`/segments/${segmentId}/news-items/${newsItemId}`);
  }

  async getArticle(articleId: string): Promise<NewsArticle> {
    const response = await this.api.get(`/news/${articleId}`);
    return response.data;
  }

  async reIngestArticle(segmentId: string, articleId: string): Promise<any> {
    const response = await this.api.post(`/segments/${segmentId}/re-ingest`, { articleId });
    return response.data;
  }

  async ingestExternalNews(segmentId: string, externalArticle: ExternalNewsArticle): Promise<any> {
    const response = await this.api.post(`/segments/${segmentId}/ingest-external`, { externalArticle });
    return response.data;
  }

  async requestScriptGeneration(segmentId: string): Promise<any> {
    const response = await this.api.post(`/segments/${segmentId}/generate-script`);
    return response.data;
  }

  async removeSegmentFromShow(segmentId: string): Promise<void> {
    await this.api.delete(`/segments/${segmentId}`);
  }

  async reorderShowSegments(showId: string, segmentOrders: Array<{segmentId: string, order: number}>): Promise<void> {
    await this.api.put(`/shows/${showId}/segments/reorder`, { segmentOrders });
  }

  async addSourceMaterial(segmentId: string, sourceMaterial: Partial<SourceMaterial>): Promise<any> {
    const response = await this.api.post(`/segments/${segmentId}/source-materials`, sourceMaterial);
    return response.data;
  }

  async searchArticles(params: { search: string; category?: string; language?: string; source?: string; published_after?: string; published_before?: string; limit?: number }): Promise<any> {
    const response = await this.api.get('/news-search/search', { params });
    return response.data;
  }

  async searchExternalNews(params: { query: string; category?: string; language?: string; source?: string; published_after?: string; published_before?: string; limit?: number }): Promise<ExternalNewsArticle[]> {
    // Map parameters to match the external news API
    const { query, category, source, ...otherParams } = params;
    const apiParams = {
      ...otherParams,
      search: query,
      categories: category, // Map category to categories
      // Note: source parameter is not supported by TheNewsAPI, so we ignore it
    };
    
    const response = await this.api.get('/external-news/search', { params: apiParams });
    // Extract the articles array from the response
    return response.data.articles || [];
  }

  async ingestExternalArticle(uuid: string, articleData: any): Promise<any> {
    const response = await this.api.post('/external-news/ingest', {
      uuid,
      article_data: articleData
    });
    return response.data;
  }

  // Alias for getShowSegment to match component expectations
  async getSegment(segmentId: string): Promise<ShowSegment> {
    return this.getShowSegment(segmentId);
  }

  // Admin APIs
  async getAdminStats(): Promise<any> {
    const response = await this.api.get('/admin/stats');
    return response.data;
  }

  async getAdminUsers(): Promise<any[]> {
    const response = await this.api.get('/admin/users');
    return response.data;
  }
}

// Create and export API service instance
export const apiService = new ApiService();

// Legacy API exports for backward compatibility
export const showsAPI = {
  getShows: () => apiService.getLegacyShows(),
  getShow: (id: string) => apiService.getLegacyShow(id),
  createShow: (data: Partial<NewsShow>) => apiService.createShow(data as any),
  updateShow: (id: string, data: Partial<NewsShow>) => apiService.updateShow(id, data as any),
  deleteShow: (id: string) => apiService.deleteShow(id),
};

export const panelistsAPI = {
  getPanelists: () => apiService.getPanelists(),
  getPanelist: (id: string) => apiService.getPanelist(id),
  createPanelist: (data: Partial<Panelist>) => apiService.createPanelist(data),
  updatePanelist: (id: string, data: Partial<Panelist>) => apiService.updatePanelist(id, data),
  deletePanelist: (id: string) => apiService.deletePanelist(id),
};

export const segmentsAPI = {
  getShowSegments: (showId: string) => apiService.getShowSegments(showId),
  getShowSegment: (segmentId: string) => apiService.getShowSegment(segmentId),
  createShowSegment: (data: Partial<ShowSegment>) => apiService.createShowSegment(data),
  updateShowSegment: (id: string, data: Partial<ShowSegment>) => apiService.updateShowSegment(id, data),
  deleteShowSegment: (id: string) => apiService.deleteShowSegment(id),
};

export const newsAPI = {
  getNewsArticles: () => apiService.getNewsArticles(),
  searchNews: (query: string) => apiService.searchNews(query),
  getExternalNews: (query: string) => apiService.getExternalNews(query),
};

export const voicesAPI = {
  getVoices: () => apiService.getVoices(),
};

export const adminAPI = {
  getStats: () => apiService.getAdminStats(),
  getUsers: () => apiService.getAdminUsers(),
};